import { httpClient, _unwrapAxiosResponse, generateRollenname } from "./http";
import { Observable, of, Subject, throwError } from "rxjs";
import { filter, switchMap, toArray } from "rxjs/operators";
import { Studio } from "./Studio";
//import { default as moment } from "moment";
import { default as moment, Moment } from "moment-timezone";
import { MonatsabschlussZeile, CalendarDay } from "./Abrechnung";

export const TIMEZONE = "Europe/Berlin";
export const DAY_FORMAT = "DD.MM.YYYY";
export const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
export const MONATSNAMEN = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

export function sortStringsAlnum(str1: string, str2: string) {
  return str1.localeCompare(str2, "de", {
      sensitivity: "base",
    });
}

export function sortDamenAlnum(dame1: Dame, dame2: Dame) {
  if (dame1 && dame2) {
    return sortStringsAlnum(dame1.kuenstlername, dame2.kuenstlername);
  }
  return 0;
}

interface AbstractEntity {
  id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Dame extends AbstractEntity {
  name: string;
  kuenstlername: string;
  geburtsdatum?: string;
  geburtsort?: string;
  nationalitaet?: string;
  steuernummer?: string;
  finanzamt?: string;
  identifikationsnummer?: string;
  ausweisnummer?: string;
  passaussteller?: string;
}

/** Eine Zeile im Dienstplan: Welche welche Dame ist in welchem Zeitraum in welchem Studio */
export interface DienstplanEintrag extends AbstractEntity {
  studio: Studio;
  /** Zeitstempel des geplanten Dienstantritts */
  von: string;
  /** Zeitstempel des geplanten Dienstschlusses */
  bis: string;
  dame: Dame;
  /** Am fraglichen Tag zu setzen. Gibt an, ob die Dame erschienen ist oder nicht. */
  dameErschienen?: boolean;
  /** Zeitstempel, wann die Dame vorzeitig gegangen ist */
  dienstAbgebrochen?: string;
}

export interface MassageArt extends AbstractEntity {
  name: string;
  preis30?: number;
  preis45?: number;
  preis60: number;
  zweiteDame?: boolean;
  validation?: (dauer: number) => boolean;
  validationMessage?: string;
}

function dauerStundenweise(dauer: number) {
  return dauer > 0 && Number.isInteger(dauer);
}

export const massagearten: MassageArt[] = [
  { id: 1, name: "Body to Body", preis30: 30, preis45: 40, preis60: 50 },
  {
    id: 2,
    name: "Blind Date",
    preis60: 50,
    validation: (dauer) => dauer == 1,
    validationMessage: "Nur 1h möglich",
  },
  { id: 3, name: "Prostata", preis30: 40, preis45: 50, preis60: 60 },
  { id: 4, name: "Lingam", preis30: 30, preis45: 45, preis60: 60 },
  {
    id: 5,
    name: "4-Hand",
    preis60: 50,
    validation: dauerStundenweise,
    zweiteDame: true,
  },
  { id: 6, name: "NURU Gel", preis60: 70, validation: dauerStundenweise },
  { id: 7, name: "Fußerotik", preis30: 40, preis45: 50, preis60: 60 },
  { id: 8, name: "Dominant", preis30: 40, preis45: 50, preis60: 60 },
  //{ id: 7, name: "Zärtliche Verführung" },
  { id: 9, name: "Tantra", preis60: 60, validation: dauerStundenweise },
  {
    id: 10,
    name: "Voyeur",
    preis60: 50,
    validation: dauerStundenweise,
    zweiteDame: true,
  }, // Farbe: #580b71
  {
    id: 11,
    name: "Paar mit 1 Dame",
    preis60: 75,
    validation: dauerStundenweise,
  },
  {
    id: 12,
    name: "Paar mit 2 Damen",
    preis60: 105,
    validation: dauerStundenweise,
    zweiteDame: true,
  },
  {
    id: 13,
    name: "Deluxe",
    preis60: 60,
    validation: (dauer) => dauer == 2,
    validationMessage: "Nur 2h möglich",
  },
];

export interface Dienst {
  von: string;
  bis: string;
}

export interface Termin extends AbstractEntity {
  studio?: Studio;
  abgesagt: boolean;
  geblockt: boolean;
  dame: Dame;
  dame2?: Dame;
  zeit: string;
  originalUhrzeit?: string;
  dauer: number;
  gast?: string;
  massageart?: MassageArt;
  preisManuell?: number;
}

export interface TerminReihe {
  dame: Dame;
  termine: Termin[];
}

export const fruehesterTermin = 10;
export const spaetesterTermin = 21.5;
export const ladenoeffnung = 10;
export const ladenoeffnungSonntag = 12;
export const ladenschluss = 21;

export type PopoverMode =
  | "info"
  | "beginnt"
  | "edit"
  | "duplicate"
  | "delete"
  | "create"
  | "block";

export type ContextMenuItems = ContextMenuItem[];
export interface ContextMenuItem {
  label: string;
  class?: string;
  icon?: string;
  style?: string;
  command?: ContextMenuItemFunction;
  url?: string;
  to?: string;
  target?: string;
  items?: ContextMenuItems;
  disabled?: boolean;
  visible: boolean;
  separator?: boolean;
}
export type ContextMenuItemFunction = (
  originalEvent: Event,
  item: ContextMenuItem
) => void;
export function formatDauerLesbar(
  dauerInStunden: number,
  minuten?: number
): string {
  if (minuten == undefined) {
    const dauerH = dauerInStunden | 0;
    const dauerM = (60 * dauerInStunden) % 60;
    return formatDauerLesbar(dauerH, dauerM);
  } else {
    let formatiert = "";
    // Stunden
    if (dauerInStunden != 0) {
      formatiert += dauerInStunden;
    }
    // Trenner
    if (dauerInStunden > 0 && minuten == 0) {
      formatiert += "h";
    }
    if (dauerInStunden > 0 && minuten > 0) {
      formatiert += ":";
    }
    // Minuten
    if (dauerInStunden == 0 && minuten > 0) {
      formatiert += minuten + "m";
    }
    if (dauerInStunden > 0 && minuten > 0) {
      formatiert += minuten;
    }
    return formatiert;
  }
}
export function currentTimestamp() {
  return moment()
    .tz(TIMEZONE)
    .minute(0)
    .second(0)
    .milliseconds(0);
}
export function getErsteAnzuzeigendeStunde(
  gesamtenTagAnzeigen: boolean
): number {
  if (gesamtenTagAnzeigen) {
    return fruehesterTermin | 0;
  }
  const aktuelleStunde = currentTimestamp().hours();
  const ersteAnzuzeigendeStunde = Math.max(
    fruehesterTermin,
    Math.min(aktuelleStunde, spaetesterTermin | 0)
  );
  return ersteAnzuzeigendeStunde;
}
export function formatTime(zeit: string, minute = "00"): string {
  if (zeit.length <= 2) {
    // Stunde + Minute sind bereits aufgeteilt (rekursiver Aufruf)
    return `${(zeit.length == 1 ? "0" : "") +
      zeit}<sup class="minuten">${(minute.length == 1 ? "0" : "") +
      minute}</sup>`;
  } else if (zeit.indexOf(":") < 3) {
    // Zeitpunkte für Kopfspalten
    const [stunde, minute] = zeit.split(":");
    return formatTime(stunde, minute);
  } else {
    // ISO Zeitstempel
    const date = new Date(zeit);
    return formatTime("" + date.getHours(), "" + date.getMinutes());
  }
}

export class TermineService {
  private getDienstplan(studio: Studio, tag: Moment, urlSuffix: string) {
    const datumString = tag.format().substring(0, 10);
    const url = `/studio/${studio.bezeichner}/dienstplan/by-${urlSuffix}?date=${datumString}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [generateRollenname(studio, "Dienstplan", "Lesezugriff")],
      //requiredRoles: `studios/lesezugriff dienstplan/lesezugriff studio/${studio.bezeichner}/dienstplan.lesezugriff`,
    } as any;
    return _unwrapAxiosResponse<DienstplanEintrag[]>(
      httpClient.get(url, scopeConfig as any)
    );
  }
  public getDienstplanKw(studio: Studio, tag: Moment) {
    return this.getDienstplan(studio, tag, "kw");
  }
  public getDienstplanTag(
    studio: Studio,
    tag: Moment,
    gesamtenTagAnzeigen: boolean
  ) {
    const dienstplan = this.getDienstplan(studio, tag, "tag");
    if (
      !gesamtenTagAnzeigen &&
      tag.format(DAY_FORMAT) == moment().format(DAY_FORMAT)
    ) {
      const jetztUnix = moment()
        .tz(TIMEZONE)
        .unix();
      const istDienstNochAktuell = (dienst: DienstplanEintrag) =>
        moment(dienst.bis)
          .tz(TIMEZONE)
          .unix() > jetztUnix;
      return dienstplan.pipe(
        // Array von Dienstplaneinträgen aufdröseln in einzelne Items
        switchMap((dienstplan) => of(...dienstplan)),
        filter(istDienstNochAktuell), // Einzelne Dienste filtern
        toArray() // Gefilterte Dienste zu Array zusammenschweißen
      );
    } else {
      return dienstplan;
    }
  }

  public getVerfuegbareDamen(studio: Studio) {
    //return of(verfuegbareDamen);
    const url = `/studio/${studio.bezeichner}/dame`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [generateRollenname(studio, "Dienstplan", "Lesezugriff")],
      //requiredRoles: `studios/lesezugriff dienstplan/lesezugriff studio/${studio.bezeichner}/dienstplan.lesezugriff damen/lesezugriff`,
    } as any;
    return _unwrapAxiosResponse<Dame[]>(httpClient.get(url, scopeConfig));
  }

  public dienstSpeichern(dienst: DienstplanEintrag) {
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [
        generateRollenname(dienst.studio, "Dienstplan", "Lesezugriff"),
        generateRollenname(dienst.studio, "Dienstplan", "Schreibzugriff"),
      ],
      //requiredRoles: `studios/lesezugriff dienstplan/schreibzugriff studio/${dienst.studio.bezeichner}/dienstplan.schreibzugriff`,
    } as any;
    if (dienst.id) {
      const url = `/studio/${dienst.studio.bezeichner}/dienstplan/${dienst.id}`;
      return _unwrapAxiosResponse<DienstplanEintrag>(
        httpClient.post(url, dienst, scopeConfig)
      );
    } else {
      const url = `/studio/${dienst.studio.bezeichner}/dienstplan`;
      return _unwrapAxiosResponse<DienstplanEintrag>(
        httpClient.put(url, dienst, scopeConfig)
      );
    }
  }
  public dienstLoeschen(dienst: DienstplanEintrag) {
    const url = `/studio/${dienst.studio.bezeichner}/dienstplan/${dienst.id}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [
        generateRollenname(dienst.studio, "Dienstplan", "Lesezugriff"),
        generateRollenname(dienst.studio, "Dienstplan", "Schreibzugriff"),
      ],
      // requiredRoles: `studios/lesezugriff dienstplan/schreibzugriff studio/${dienst.studio.bezeichner}/dienstplan.schreibzugriff`,
    } as any;
    const resultSubject = new Subject<any>();
    _unwrapAxiosResponse(httpClient.delete(url, scopeConfig)).subscribe(
      (_) => {
        resultSubject.next(true);
        resultSubject.complete();
      },
      (error) => {
        resultSubject.error(error);
      }
    );
    return resultSubject;
  }

  public getTermine(
    studio: Studio,
    datum: Moment,
    gesamtenTagAnzeigen: boolean
  ): Observable<TerminReihe[]> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }

    const datumString = datum.format().substring(0, 10);
    const url = `/studio/${studio.bezeichner}/termin?date=${datumString}&gesamtenTagAnzeigen=${gesamtenTagAnzeigen}`;

    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [
        generateRollenname(studio, "Dienstplan", "Lesezugriff"),
        generateRollenname(studio, "Terminplan", "Lesezugriff"),
      ],
    } as any;
    const termineSubject = new Subject<TerminReihe[]>();
    _unwrapAxiosResponse<Termin[]>(httpClient.get(url, scopeConfig)).subscribe(
      (termine) => {
        const termineJeDame: { [key: string]: TerminReihe } = {};
        termine.forEach((termin) => {
          const unassignedDameKey = "unassigned";
          const dameKey = termin.dame ? "" + termin.dame.id : unassignedDameKey;
          if (!termineJeDame[dameKey]) {
            termineJeDame[dameKey] = { dame: termin.dame, termine: [] };
          }
          termineJeDame[dameKey].termine.push(termin);

          // Ist der Termin zusätzlich einer zweiten Dame zugeordnet?
          if (termin.dame2) {
            const dame2Key = "" + termin.dame2.id;
            if (!termineJeDame[dame2Key]) {
              termineJeDame[dame2Key] = { dame: termin.dame2, termine: [] };
            }
            termineJeDame[dame2Key].termine.push(termin);
          }
        });

        // Terminreihen zusammenstellen
        const alleTermine: TerminReihe[] = [];
        for (const dameKey in termineJeDame) {
          const termineDerDame = termineJeDame[dameKey];
          // Massageart aus ID bestimmen
          termineDerDame.termine.forEach((termin) => {
            const neueMassagearten = massagearten.filter(
              (art) => art.id == termin.massageart
            );
            if (neueMassagearten.length) {
              termin.massageart = neueMassagearten[0];
            }
          });
          // Termine nach Zeit sortieren
          termineDerDame.termine.sort(
            (a, b) =>
              moment(a.zeit)
                .tz(TIMEZONE)
                .unix() -
              moment(b.zeit)
                .tz(TIMEZONE)
                .unix()
          );
          alleTermine.push(termineDerDame);
        }

        termineSubject.next(alleTermine);
        termineSubject.complete();
      },
      (error) => {
        termineSubject.error(error);
      }
    );

    //return this.randomizeTermine();

    return termineSubject;
  }

  public getTermineMonat(
    studio: Studio,
    monat: Moment,
    alleDamenZurueckgeben: boolean,
    dame?: Dame
  ): Observable<MonatsabschlussZeile[]> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const datumString = monat.format().substring(0, 7);
    let url = `/studio/${studio.bezeichner}/termin/monat?monat=${datumString}&alleDamenZurueckgeben=${alleDamenZurueckgeben}`;
    if (dame && dame.id) {
      url += `&dame=${dame.id}`;
    }
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;
    return _unwrapAxiosResponse<MonatsabschlussZeile[]>(
      httpClient.get(url, scopeConfig)
    );
  }
  public buchungstagVerschieben(
    studio: Studio,
    monat: Moment,
    dame: Dame,
    vonTag: string,
    nachTag: string,
    alleDamenZurueckgeben: boolean
  ): Observable<MonatsabschlussZeile[] | string> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const datumString = monat.format().substring(0, 7);
    const url = `/studio/${studio.bezeichner}/termin/monat/verschieben?monat=${datumString}&dame=${dame.id}&vonTag=${vonTag}&nachTag=${nachTag}&alleDamenZurueckgeben=${alleDamenZurueckgeben}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;
    return _unwrapAxiosResponse<MonatsabschlussZeile[]>(httpClient.post(url, null, scopeConfig));
  }
  public buchungstagSpeichern(
    studio: Studio,
    monat: Moment,
    dame: Dame,
    tag: CalendarDay
  ): Observable<void> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const datumString = monat.format().substring(0, 7);
    const url = `/studio/${studio.bezeichner}/termin/monat?monat=${datumString}&dame=${dame.id}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;

    const data = {
      studio,
      tag: tag.tag,
      unterschriftDame: tag.unterschriftDame,
      unterschriftStudio: tag.unterschriftStudio,
    };

    return _unwrapAxiosResponse<void>(httpClient.post(url, data, scopeConfig));
  }
  public terminSpeichern(studio: Studio, termin: Termin): Observable<Termin> {
    termin.studio = studio;

    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [
        generateRollenname(studio, "Dienstplan", "Lesezugriff"),
        generateRollenname(studio, "Terminplan", "Schreibzugriff"),
      ],
      // requiredRoles: `studios/lesezugriff termine/schreibzugriff studio/${studio.bezeichner}/termine.schreibzugriff`,
    } as any;

    let axiosResponse;
    if (termin.id) {
      axiosResponse = httpClient.post(
        `/studio/${studio.bezeichner}/termin/${termin.id}`,
        termin,
        scopeConfig
      );
    } else {
      axiosResponse = httpClient.put(
        `/studio/${studio.bezeichner}/termin`,
        termin,
        scopeConfig
      );
    }

    return _unwrapAxiosResponse<Termin>(axiosResponse);
  }

  public termineAufHeute(studio: Studio): Observable<Termin[]> {
    const scopeConfigRead = {
      requiredScope: "openid profile",
      requiredRoles: [
        generateRollenname(studio, "Dienstplan", "Lesezugriff"),
        generateRollenname(studio, "Terminplan", "Lesezugriff"),
      ],
      // requiredRoles: `studios/lesezugriff termine/lesezugriff studio/${studio.bezeichner}/termine.lesezugriff`,
    } as any;

    const scopeConfigWrite = {
      requiredScope: "openid profile",
      requiredRoles: [
        generateRollenname(studio, "Dienstplan", "Lesezugriff"),
        generateRollenname(studio, "Terminplan", "Schreibzugriff"),
      ],
      // requiredRoles: `studios/lesezugriff termine/schreibzugriff studio/${studio.bezeichner}/termine.schreibzugriff`,
    } as any;

    const datumGestern = currentTimestamp()
      .subtract(1, "day")
      .format()
      .substring(0, 5);

    return _unwrapAxiosResponse<Termin[]>(
      httpClient.get(
        `/studio/${studio.bezeichner}/termin?date=${datumGestern}`,
        scopeConfigRead
      )
    ).pipe(
      // Array von Terminen aufdröseln in einzelne Items
      switchMap((termine) => of(...termine)),

      // Jeden einzelnen Termin aktualisieren
      switchMap((termin) => {
        console.debug(`Verschiebe Termin ${termin.id} von gestern auf heute`);

        const zeit = moment(termin.zeit).tz(TIMEZONE);
        termin.zeit = currentTimestamp()
          .hour(zeit.hour())
          .minute(zeit.minute())
          .format();

        return _unwrapAxiosResponse<Termin>(
          httpClient.post(
            `/studio/${studio.bezeichner}/termin/${termin.id}`,
            termin,
            scopeConfigWrite
          )
        );
      }),

      // Alle einzelnen Terminaktualisierungen zu Array zusammenfassen
      toArray()
    );
  }
  public tagesTermineLoeschen(studio: Studio, tag: Moment): Observable<void> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const datumString = tag.format().substring(0, 10);
    const url = `/studio/${studio.bezeichner}/tag?tag=${datumString}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung/Loeschen"],
    } as any;

    return _unwrapAxiosResponse<void>(httpClient.delete(url, scopeConfig));
  }
  public alleBuchungsdatenLoeschen(): Observable<void> {
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung/Loeschen"],
    } as any;

    return _unwrapAxiosResponse<void>(httpClient.delete('/notfall/alle-daten', scopeConfig));
  }
}
